import { Logger } from '../appinsights/Logger'
import { rockefeller } from '../environment/Config.json'
import type { RootState } from '../../store'
import { TotpSessionKey, UAuthTokenKey } from '../../shared/Constants'
import { setSessionStorage } from '../../shared/utilities/Session'
import { SetUAuthLoginData } from '../../store/slice'
import { storeRef } from '../../store/storeRef'

/** Seconds before expiry at which a proactive silent refresh is triggered. */
const REFRESH_THRESHOLD_SECONDS = 60

/** Decodes the `exp` claim from a JWT without verifying the signature. Returns 0 on failure. */
function decodeExp(token: string): number {
  try {
    const payload = token.split('.')[1]
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
    return typeof json.exp === 'number' ? json.exp : 0
  } catch {
    return 0
  }
}

/** Returns true when the token is missing, unparseable, or within the refresh threshold. */
function isNearExpiry(token: string): boolean {
  const exp = decodeExp(token)
  if (exp === 0) return true
  return exp - Date.now() / 1000 < REFRESH_THRESHOLD_SECONDS
}

export const prepareHeaders = async (headers: Headers, api: { getState: () => unknown }) => {
  const state = api.getState() as RootState
  if (state.app.uAuthAccessToken) {
    headers.append('Authorization', `Bearer ${state.app.uAuthAccessToken}`)
  }
  headers.append('Content-Type', 'application/json; charset=UTF-8;')
  headers.append('cache-control', 'no-cache')
  headers.append('profilejson', state.app.profileKey ?? '')
  headers.append('contextjson', state.app.contextKey ?? '')

  if (state.app.totpSessionKey) {
    headers.append(TotpSessionKey, state.app.totpSessionKey)
  }

  if (rockefeller.api.version != null && rockefeller.api.version.trim() !== '') {
    headers.append('version', rockefeller.api.version)
  }
  return headers
}

export class UAuthToken {
  /** In-flight refresh promise — deduplicates concurrent callers so only one network call is made. */
  private static _refreshPromise: Promise<string> | null = null

  /**
   * Returns a valid access token.
   * - If the current token is still fresh (not near expiry), it is returned as-is.
   * - If a refresh is already in flight, the in-flight promise is awaited (deduplicated).
   * - Otherwise a new silent refresh is initiated, and the refreshed token is persisted
   *   back to Redux state and sessionStorage via storeRef.
   */
  static async getAccessToken(currentToken: string): Promise<string> {
    if (!isNearExpiry(currentToken)) {
      return currentToken
    }

    if (this._refreshPromise) {
      return this._refreshPromise
    }

    this._refreshPromise = this._doSilentRefresh(currentToken)
      .then((newToken) => {
        this._refreshPromise = null
        storeRef.dispatch(SetUAuthLoginData(newToken))
        setSessionStorage(UAuthTokenKey, newToken)
        return newToken
      })
      .catch((error) => {
        this._refreshPromise = null
        throw error
      })

    return this._refreshPromise
  }

  private static async _doSilentRefresh(currentToken: string): Promise<string> {
    try {
      const response = await fetch(`${rockefeller.api.baseurl}/multifactorauthapi/api/UnifiedAuth/silent`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${currentToken}`,
          'Content-Type': 'application/json; charset=UTF-8;',
          'cache-control': 'no-cache'
        }
      })

      if (!response.ok) {
        Logger.LogInfo({ message: `UAuthToken silent refresh failed with status ${response.status}` })
        throw new Error(`Silent refresh failed: ${response.status}`)
      }

      const { accessToken } = (await response.json()) as { accessToken: string }
      return accessToken
    } catch (error) {
      Logger.LogException({ exceptionType: 'UAUTH_SILENT_REFRESH', message: (error as Error).message })
      throw error
    }
  }

  static async getOBOAccessToken(userAssertion: string): Promise<string> {
    try {
      const accessToken = await this.getAccessToken(userAssertion)
      const response = await fetch(`${rockefeller.api.baseurl}/multifactorauthapi/api/UnifiedAuth/obo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=UTF-8;',
          'cache-control': 'no-cache',
          Authorization: `Bearer ${accessToken}`
        },
        body: JSON.stringify({ scope: 'api://target-api/.default' })
      })

      if (!response.ok) {
        Logger.LogInfo({ message: `UAuthToken OBO request failed with status ${response.status}` })
        throw new Error(`OBO request failed: ${response.status}`)
      }

      const { OBOToken } = (await response.json()) as { OBOToken: string }
      return OBOToken
    } catch (error) {
      Logger.LogException({ exceptionType: 'UAUTH_OBO', message: (error as Error).message })
      throw error
    }
  }
}
